import { create } from 'zustand';
import type { HistoricoArete } from '../../api-client/pre-compras/aretes.dto';
import {
  obtenerHistorialAreteSiiniga,
  obtenerHistorialAreteTemporal
} from '../../api-client/pre-compras/pre-compras.api';
import { makeGet, makePatch, makePost } from '../../utils/call-api-endpoint';
import type { PaginatedList } from '../common/padinated-list';
import { type SortDir } from '../common/pagination-query-params';
import { useUIStore } from '../ui/ui.store';
import type { PartidaPreCompra, PreCompra, PreCompraBusqueda } from './pre-compra';

export interface PreCompraState {
  list: PaginatedList<PreCompraBusqueda>;
  current: PreCompra;
  historialSiiniga: Array<HistoricoArete>;
  historialTemporal: Array<HistoricoArete>;

  nueva: () => Promise<void>;
  cargarPaginadas: (params: PreComprasPaginadasQueryParams) => Promise<void>;
  cargarPorId: (id: string) => Promise<void>;
  cargarHistorialSiiniga: (folio: string, partida: number) => Promise<void>;
  cargarHistorialTemporal: (folio: string, partida: number) => Promise<void>;
  registrar: (request: RegistrarPreCompraRequest) => Promise<boolean>;
  actualizar: (id: string, request: ActualizarPreCompraRequest) => Promise<boolean>;
  cancelar: (id: string) => Promise<boolean>;
  siguienteFolio: () => Promise<string>;
}

export interface RegistrarPreCompraRequest {
  fecha: Date;
  referencia: string;
  idPersonaVende: number;
  idPersonaRecibe: number;
  idPersonaEntrega: number;
  idCorral: number;
  idZona: number;
  totalAnimales: number;
  partidas: Array<Omit<PartidaPreCompra, 'partida'>>;
}

export interface ActualizarPreCompraRequest {
  fecha: Date;
  referencia: string;
  idPersonaVende: number;
  idPersonaRecibe: number;
  idPersonaEntrega: number;
  idCorral: number;
  totalAnimales: number;
  partidas: Array<PartidaPreCompra>;
}

export interface PreComprasPaginadasQueryParams {
  fechaDesde: Date;
  fechaHasta: Date;
  filtro: string;
  columnaFiltro: string;
  columnaOrden: string;
  direccionOrden: SortDir;
  limite: number;
  pagina: number;
}

function toQueryString(params: PreComprasPaginadasQueryParams) {
  let {
    fechaDesde,
    fechaHasta,
    columnaFiltro,
    columnaOrden,
    direccionOrden,
    filtro,
    pagina,
    limite
  } = params;

  columnaFiltro ??= 'nombre';
  columnaOrden ??= 'clave';
  direccionOrden ??= 'asc';
  filtro ??= '';
  pagina ??= 0;
  limite ??= 10;

  let queryString =
    `fechaDesde=${fechaDesde.toISOString()}&fechaHasta=${fechaHasta.toISOString()}` +
    `&columnaFiltro=${columnaFiltro}&columnnaOrden=${columnaOrden}&direccionOrden=${direccionOrden}` +
    `&filtro=${filtro}&pagina=${pagina}&limite=${limite}`;

  return queryString;
}

const { showSuccessToast } = useUIStore.getState();

export const usePreComprasStore = create<PreCompraState>()((set, get) => ({
  list: {
    items: [],
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0
  },
  current: { activa: true } as PreCompra,
  historialSiiniga: [],
  historialTemporal: [],

  nueva: async () => {
    const data = await makeGet<string>(`/pre-compras/siguiente-folio`);

    set({
      current: {
        folioInterno: data.result,
        partidas: [] as Array<PartidaPreCompra>,
        activa: true
      } as PreCompra,
      historialSiiniga: [],
      historialTemporal: []
    });
  },
  cargarPaginadas: async (params: PreComprasPaginadasQueryParams) => {
    const data = await makeGet<PaginatedList<PreCompraBusqueda>>(
      `/pre-compras/paginadas?${toQueryString(params)}`
    );

    if (data.isFailure) return;

    set({
      list: data.result
    });
  },
  cargarPorId: async (id: string) => {
    const data = await makeGet<PreCompra>(`/pre-compras/${id}`);

    if (data.isFailure) return;

    set({
      current: data.result
    });
  },
  cargarHistorialSiiniga: async (folio: string, partida: number) => {
    const response = await obtenerHistorialAreteSiiniga(folio, partida);

    if (response.isFailure) return;

    set({
      historialSiiniga: response.result
    });
  },
  cargarHistorialTemporal: async (folio: string, partida: number) => {
    const response = await obtenerHistorialAreteTemporal(folio, partida);

    if (response.isFailure) return;

    set({
      historialTemporal: response.result
    });
  },
  registrar: async (request: RegistrarPreCompraRequest) => {
    const data = await makePost<RegistrarPreCompraRequest, PreCompra>('/pre-compras/', request);

    if (data.isFailure) return false;

    showSuccessToast('Pre-compra registrada');

    set({
      current: {} as PreCompra
    });

    return true;
  },
  actualizar: async (id: string, request: ActualizarPreCompraRequest) => {
    const data = await makePatch<ActualizarPreCompraRequest, PreCompra>(
      `/pre-compras/${id}`,
      request
    );

    if (data.isFailure) return false;

    showSuccessToast('Pre-compra actualizada');

    set({
      current: {
        ...get().current,
        ...{
          referencia: request.referencia,
          idCorral: request.idCorral,
          idPersonaEntrega: request.idPersonaEntrega,
          idPersonaRecibe: request.idPersonaRecibe,
          idPersonaVende: request.idPersonaVende,
          fecha: request.fecha,
          totalAnimales: request.totalAnimales,
          partidas: request.partidas
        }
      }
    });

    return true;
  },
  cancelar: async (id: string) => {
    const data = await makePatch(`/pre-compras/${id}/cancelar`);

    if (data.isFailure) return false;

    showSuccessToast('Pre-compra cancelada');

    set({
      current: {
        ...get().current,
        activa: false
      }
    });

    return true;
  },
  siguienteFolio: async () => {
    const data = await makeGet<string>(`/pre-compras/siguiente-folio`);

    set({
      current: {
        ...get().current,
        folioInterno: data.result
      }
    });

    return data.result;
  }
}));
